/**
 * TADS に対する時間範囲指定。api-server の
 * `openapi/sheetsui_api/resources/data_raw/tad_time_range.yml` に対応する。
 *
 * 保持しているのは日時ではなく「日時の作り方(相対式)」で、epoch ミリ秒への解決は
 * クライアントの責務 (LDS は時刻を解決せず、時間経過にも追随しない)。
 *
 * 間違えやすい点: `origin: null` は評価時の現在時刻、オフセット成分の `null` は
 * 「0 ずらす」ではなく絶対値 (月日は `01`、時分秒は `00`)、`offsetYears` は常に相対、
 * LDS 側の `isExclusive` は `isClosed` と論理が反転する。
 */

export const tadTimeZoneList = ["JST", "UTC"] as const;
export type TadTimeZone = (typeof tadTimeZoneList)[number];

/**
 * 読み取り時の最大行数。LDS 側の `TADSRefWindowMaxLimit` と揃えている。
 *
 * 超過分は新しい側が落ちる (窓読みもダウンロードも `ORDER BY time_col, row_id LIMIT n`)。
 * 打ち切りは通知されないため、行数が limit ちょうどなら続きがある可能性を示す必要がある。
 */
export const TAD_TIME_RANGE_MAX_LIMIT = 10000;
export const TAD_TIME_RANGE_MIN_LIMIT = 1;

/** 時間範囲の端点。 */
export type TadTimeEndpoint = {
  /** true=閉端点(含む) / false=開端点(含まない) */
  isClosed: boolean;
  /** 原点時刻 `"YYYY-MM-DD hh:mm:ss"`。`MM` 以降は省略可。`null` は評価時の現在時刻。 */
  origin: string | null;
  /** 常に相対 (省略できない唯一のオフセット成分)。 */
  offsetYears: number;
  /** 以下、`null` は相対値ではなく絶対値 (月日は `01`、時分秒は `00`)。 */
  offsetMonths: number | null;
  offsetDays: number | null;
  offsetHours: number | null;
  offsetMinutes: number | null;
  offsetSeconds: number | null;
};

/**
 * 時間軸カラムの範囲。`start` / `end` は個別に省略できるが少なくとも一方は必須
 * (範囲指定なしの全件取得は提供されない)。型で表せないので `getTadTimeRangeErrors` で検証する。
 */
export type TadTimeRange = {
  /** 開始の端点。`null` の場合は過去方向に非有界。 */
  start: TadTimeEndpoint | null;
  /** 終了の端点。`null` の場合は未来方向に非有界。 */
  end: TadTimeEndpoint | null;
  /** 端点時刻の評価に用いるタイムゾーン。 */
  timezone: TadTimeZone;
  /** 読み取り時の最大行数(1〜10000)。 */
  limit: number;
};

type TadTimeEndpointRaw = {
  is_closed: boolean;
  origin: string | null;
  offset_years: number;
  offset_months: number | null;
  offset_days: number | null;
  offset_hours: number | null;
  offset_minutes: number | null;
  offset_seconds: number | null;
};

export type TadTimeRangeRaw = {
  start: TadTimeEndpointRaw | null;
  end: TadTimeEndpointRaw | null;
  timezone: TadTimeZone;
  limit: number;
};

const encodeTadTimeEndpoint = ({
  isClosed,
  origin,
  offsetYears,
  offsetMonths,
  offsetDays,
  offsetHours,
  offsetMinutes,
  offsetSeconds,
}: TadTimeEndpoint): TadTimeEndpointRaw => ({
  is_closed: isClosed,
  origin,
  offset_years: offsetYears,
  offset_months: offsetMonths,
  offset_days: offsetDays,
  offset_hours: offsetHours,
  offset_minutes: offsetMinutes,
  offset_seconds: offsetSeconds,
});

const parseTadTimeEndpoint = ({
  is_closed,
  origin,
  offset_years,
  offset_months,
  offset_days,
  offset_hours,
  offset_minutes,
  offset_seconds,
}: TadTimeEndpointRaw): TadTimeEndpoint => ({
  isClosed: is_closed,
  origin,
  offsetYears: offset_years,
  // null は「0 ずらす」ではなく絶対値。既定値 0 に倒すと意味が変わる。
  offsetMonths: offset_months,
  offsetDays: offset_days,
  offsetHours: offset_hours,
  offsetMinutes: offset_minutes,
  offsetSeconds: offset_seconds,
});

export const encodeTadTimeRange = ({
  start,
  end,
  timezone,
  limit,
}: TadTimeRange): TadTimeRangeRaw => ({
  start: start ? encodeTadTimeEndpoint(start) : null,
  end: end ? encodeTadTimeEndpoint(end) : null,
  timezone,
  limit,
});

export const parseTadTimeRange = ({
  start,
  end,
  timezone,
  limit,
}: TadTimeRangeRaw): TadTimeRange => ({
  start: start ? parseTadTimeEndpoint(start) : null,
  end: end ? parseTadTimeEndpoint(end) : null,
  timezone,
  limit,
});

const ORIGIN_PATTERN =
  /^(\d{4})(?:-(\d{2})(?:-(\d{2})(?: (\d{2})(?::(\d{2})(?::(\d{2}))?)?)?)?)?$/;

export const isValidTadTimeRangeLimit = (limit: number): boolean =>
  Number.isInteger(limit) &&
  limit >= TAD_TIME_RANGE_MIN_LIMIT &&
  limit <= TAD_TIME_RANGE_MAX_LIMIT;

const isValidTadTimeOrigin = (origin: string): boolean =>
  ORIGIN_PATTERN.test(origin.trim());

/**
 * 時間範囲の入力エラー。フォーム表示・保存可否・解決前チェックの単一の入口。
 * 原点の書式は api-server の `tadTimeOriginLayouts` に合わせる (`T` 区切りは不可)。
 */
export const getTadTimeRangeErrors = (timeRange: TadTimeRange): string[] => {
  const errors: string[] = [];
  if (timeRange.start === null && timeRange.end === null) {
    errors.push("開始・終了のどちらか一方は指定してください");
  }
  // 欠損や範囲外を通すと LDS の汎用 rejected に潰れ、打ち切り推定
  // (rows.length >= limit) も常に false になって警告が出なくなる。
  if (!isValidTadTimeRangeLimit(timeRange.limit)) {
    errors.push(
      `行数の上限は ${TAD_TIME_RANGE_MIN_LIMIT}〜${TAD_TIME_RANGE_MAX_LIMIT} で指定してください`
    );
  }
  if (
    timeRange.start?.origin != null &&
    !isValidTadTimeOrigin(timeRange.start.origin)
  ) {
    errors.push("開始の原点の形式が正しくありません");
  }
  if (
    timeRange.end?.origin != null &&
    !isValidTadTimeOrigin(timeRange.end.origin)
  ) {
    errors.push("終了の原点の形式が正しくありません");
  }
  return errors;
};

export const isValidTadTimeRange = (timeRange: TadTimeRange): boolean =>
  getTadTimeRangeErrors(timeRange).length === 0;

// 解決 (相対式 → 絶対時刻)。規則は ADR (3) 時間範囲指定 に従う。

/** 解決済みの端点。LDS へ渡す形(`isExclusive` は `isClosed` の反転)。 */
export type ResolvedTadTimeEndpoint = {
  /** epoch ミリ秒(UTC) */
  time: number;
  isExclusive: boolean;
};

export type ResolvedTadTimeRange = {
  startsAt: ResolvedTadTimeEndpoint | null;
  endsAt: ResolvedTadTimeEndpoint | null;
};

/**
 * - `origin-component-truncated`
 *   原点で明示されている成分が、オフセット側で省略(null = 絶対値)されているため捨てられる
 * - `start-after-end` 評価結果が 過去 > 未来 になっている
 * - `may-become-start-after-end` 時間経過によって 過去 > 未来 になりえる
 */
export type TadTimeRangeWarning =
  | "origin-component-truncated"
  | "start-after-end"
  | "may-become-start-after-end";

export type ResolvedTadTimeRangeResult = {
  timeRange: ResolvedTadTimeRange;
  limit: number;
  /** 解決に使った基準時刻(epoch ミリ秒)。「いつ時点の窓か」の表示用 */
  evaluatedAt: number;
  /**
   * 窓が次に変わる時刻(epoch ミリ秒)。`null` は「変わらない」(両端とも固定原点)。
   * 再 listen のタイマーをここに張る。
   */
  nextBoundaryAt: number | null;
  warnings: TadTimeRangeWarning[];
};

/** JST は UTC+09:00 固定(DST なし)なので固定オフセットで足りる。 */
export const TIMEZONE_OFFSET_MINUTES: Record<TadTimeZone, number> = {
  UTC: 0,
  JST: 9 * 60,
};

/** 時刻の成分。month は 1-12。 */
type TimeComponents = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
};

/** 粒度。細かい順。 */
const granularityList = [
  "second",
  "minute",
  "hour",
  "day",
  "month",
  "year",
] as const;
type Granularity = (typeof granularityList)[number];

const toComponents = (
  epochMs: number,
  timezone: TadTimeZone
): TimeComponents => {
  const shifted = new Date(
    epochMs + TIMEZONE_OFFSET_MINUTES[timezone] * 60_000
  );
  return {
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate(),
    hour: shifted.getUTCHours(),
    minute: shifted.getUTCMinutes(),
    second: shifted.getUTCSeconds(),
  };
};

const pad = (value: number) => String(value).padStart(2, "0");

/**
 * epoch ms を指定タイムゾーンで `YYYY-MM-DD hh:mm:ss` に整形する。
 * 表示だけでなく `toAbsoluteTadTimeRange` が原点文字列を作るのにも使うため、
 * 書式は api-server の `tadTimeOriginLayouts` が受理する形から動かさないこと。
 */
export const formatTadInstant = (
  epochMs: number,
  timezone: TadTimeZone
): string => {
  const { year, month, day, hour, minute, second } = toComponents(
    epochMs,
    timezone
  );
  return `${year}-${pad(month)}-${pad(day)} ${pad(hour)}:${pad(minute)}:${pad(second)}`;
};

/**
 * 原点を `<input type="datetime-local">` の値へ変換する (`null` は空文字列)。
 * 保存済みの部分指定 (`2026-05` 等) はブラウザが解釈できないので秒まで補う。
 */
export const toDateTimeLocalValue = (origin: string | null): string => {
  if (origin === null) return "";
  const [date = "", time = ""] = origin.trim().split(" ");
  const [year = "", month = "01", day = "01"] = date.split("-");
  const [hour = "00", minute = "00", second = "00"] = time
    ? time.split(":")
    : [];
  return `${year}-${month}-${day}T${hour}:${minute}:${second}`;
};

/**
 * `<input type="datetime-local">` の値を原点へ変換する。秒が省かれることがあるため補う。
 * 区切りが空白なのは api-server の `tadTimeOriginLayouts` が空白しか解釈しないため。
 */
export const fromDateTimeLocalValue = (localValue: string): string => {
  const [date = "", time = ""] = localValue.split("T");
  const [hour = "00", minute = "00", second = "00"] = time
    ? time.split(":")
    : [];
  return `${date} ${hour}:${minute}:${second}`;
};

const fromComponents = (c: TimeComponents, timezone: TadTimeZone): number =>
  Date.UTC(c.year, c.month - 1, c.day, c.hour, c.minute, c.second) -
  TIMEZONE_OFFSET_MINUTES[timezone] * 60_000;

type OriginComponentKey = Exclude<keyof TimeComponents, "year">;

export class TadTimeRangeResolveError extends Error {}

const parseOrigin = (origin: string): TimeComponents => {
  const matched = ORIGIN_PATTERN.exec(origin.trim());
  if (!matched) {
    throw new TadTimeRangeResolveError(
      `invalid origin format: ${JSON.stringify(origin)}`
    );
  }
  const [, year, month, day, hour, minute, second] = matched;
  // 後ろの成分の省略は保存済みドキュメント由来でありうる。埋める既定値は api-server の
  // `tadTimeOriginLayouts` が省略時に埋める値と同じ。
  return {
    year: Number(year),
    month: month === undefined ? 1 : Number(month),
    day: day === undefined ? 1 : Number(day),
    hour: hour === undefined ? 0 : Number(hour),
    minute: minute === undefined ? 0 : Number(minute),
    second: second === undefined ? 0 : Number(second),
  };
};

const offsetOf = (
  endpoint: TadTimeEndpoint
): Record<OriginComponentKey, number | null> => ({
  month: endpoint.offsetMonths,
  day: endpoint.offsetDays,
  hour: endpoint.offsetHours,
  minute: endpoint.offsetMinutes,
  second: endpoint.offsetSeconds,
});

/** オフセットが `null` のときに使う絶対値。 */
const ABSOLUTE_DEFAULT: Record<OriginComponentKey, number> = {
  month: 1,
  day: 1,
  hour: 0,
  minute: 0,
  second: 0,
};

const MS_PER_SECOND = 1_000;
const MS_PER_MINUTE = 60 * MS_PER_SECOND;
const MS_PER_HOUR = 60 * MS_PER_MINUTE;
const MS_PER_DAY = 24 * MS_PER_HOUR;

const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31] as const;

const isLeapYear = (year: number): boolean =>
  (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;

/**
 * `month`(1-12) の末日。`Date` を経由しないのは、`Date.UTC` が年 0〜99 を 1900+year へ
 * 写す仕様のせいで閏年判定がずれるため。
 */
const lastDayOfMonth = (year: number, month: number): number =>
  month === 2 && isLeapYear(year) ? 29 : DAYS_IN_MONTH[month - 1];

/**
 * `year`/`month`(1-12) に `months` ヶ月を暦加算し、対象月に `day` が無ければ月末へ丸める。
 *
 * 繰り上がり(`2/31` → `3/3`)ではなくクランプを選ぶのは、結果が意図した月に収まり、
 * 基準時刻に対する解決の単調性が保たれるため。繰り上がりだと `origin: null` の
 * 「1ヶ月前の同日」が 3/31 に 3/3、翌 4/1 に 3/1 と**過去へ戻って**しまう。
 * api-server の `addTadMonthsClamped`(server/api/domain/model/tad_time_range.go)と同一規則。
 */
const addMonthsClamped = (
  year: number,
  month: number,
  day: number,
  months: number
): { year: number; month: number; day: number } => {
  const total = year * 12 + (month - 1) + months;
  const targetYear = Math.floor(total / 12);
  const targetMonth = total - targetYear * 12 + 1;
  return {
    year: targetYear,
    month: targetMonth,
    day: Math.min(day, lastDayOfMonth(targetYear, targetMonth)),
  };
};

/**
 * 窓が動く粒度。解決結果が `now` に依存するのはオフセットが非 null の成分だけなので、
 * 粒度は「非 null である最も細かい成分」で決まる。
 */
const driftGranularityOf = (endpoint: TadTimeEndpoint): Granularity => {
  // 原点が固定されている端点は now に依存しない (呼び出し側で除外される)
  if (endpoint.origin !== null) return "year";
  const offset = offsetOf(endpoint);
  if (offset.second !== null) return "second";
  if (offset.minute !== null) return "minute";
  if (offset.hour !== null) return "hour";
  if (offset.day !== null) return "day";
  if (offset.month !== null) return "month";
  return "year";
};

/**
 * 指定粒度の「次の境界時刻」を返す。
 *
 * 秒粒度の窓は本来 1 秒ごとに動くが、その頻度で listen を張り替えるのは現実的でないので
 * 分境界へ丸める。窓の端点自体は秒まで正確で、追随が最大 1 分遅れるだけになる。
 * 丸めずに「追随不能」にすると、「現在時刻〜1分後」は毎分更新されるのに
 * 「現在時刻〜1分10秒後」は一切更新されない、という指定の細かさで挙動が飛ぶ状態になる。
 */
const nextBoundaryOf = (
  nowMs: number,
  timezone: TadTimeZone,
  granularity: Granularity
): number => {
  const c = toComponents(nowMs, timezone);
  const next: TimeComponents = { ...c, second: 0 };
  switch (granularity) {
    // 秒粒度も分境界まで丸める
    case "second":
    case "minute":
      next.minute = c.minute + 1;
      break;
    case "hour":
      next.hour = c.hour + 1;
      next.minute = 0;
      break;
    case "day":
      next.day = c.day + 1;
      next.hour = 0;
      next.minute = 0;
      break;
    case "month":
      next.month = c.month + 1;
      next.day = 1;
      next.hour = 0;
      next.minute = 0;
      break;
    case "year":
      next.year = c.year + 1;
      next.month = 1;
      next.day = 1;
      next.hour = 0;
      next.minute = 0;
      break;
  }
  return fromComponents(next, timezone);
};

/**
 * 端点を絶対時刻へ解決する。適用順序は
 * 「スナップ(null 成分の絶対値代入) → 年月の一括暦加算(月末クランプ) → 日以下の純算術」。
 *
 * スナップを先に行うのは、捨てると宣言された成分が年月加算の桁上がりを介して結果に
 * 影響しないようにするため(1/31 起点の `(0, +1, null)`「翌月頭」は 2/1 であって 3/1 ではない)。
 * 年月を一括で足すのは、逐次適用がクランプを二重に発生させるため
 * (`2024-02-29` の `(1, +1, 0)` は 2025-03-29。逐次だと 2/28 → 3/28 になる)。
 *
 * 同一の規則が api-server(`server/api/domain/model/tad_time_range.go` の
 * `resolveTadTimeEndpoint`)と LDS にもあり、変更する際は揃えること。
 */
const resolveEndpoint = (
  endpoint: TadTimeEndpoint,
  nowMs: number,
  timezone: TadTimeZone
): { time: number; truncatedOrigin: boolean } => {
  const origin = endpoint.origin;
  const base =
    origin !== null ? parseOrigin(origin) : toComponents(nowMs, timezone);
  const offset = offsetOf(endpoint);

  // 1) スナップ: オフセットが null (= 相対値ではなく絶対値) の成分を既定値に置き換える。
  const snapped: TimeComponents = { ...base };
  let truncatedOrigin = false;
  for (const key of ["month", "day", "hour", "minute", "second"] as const) {
    if (offset[key] !== null) continue;
    // 既定値と同じなら捨てても結果が変わらないので警告しない。
    if (origin !== null && base[key] !== ABSOLUTE_DEFAULT[key]) {
      truncatedOrigin = true;
    }
    snapped[key] = ABSOLUTE_DEFAULT[key];
  }

  // 2) 年・月は 1 回の暦加算にまとめる。年は常に相対(nullable でない)。
  const shifted = addMonthsClamped(
    snapped.year,
    snapped.month,
    snapped.day,
    endpoint.offsetYears * 12 + (offset.month ?? 0)
  );

  // 3) 日以下は純算術。日の加算が月を跨ぐのは ADR の繰り上がり規則どおり
  //    (`2026-05-20` の `(1, 1, 11)` → `2027-06-31` → `2027-07-01`)。
  //    JST/UTC はいずれも固定オフセット(DST なし)なので、ミリ秒加算で暦加算と一致する。
  const time =
    fromComponents(
      {
        year: shifted.year,
        month: shifted.month,
        day: shifted.day,
        hour: snapped.hour,
        minute: snapped.minute,
        second: snapped.second,
      },
      timezone
    ) +
    (offset.day ?? 0) * MS_PER_DAY +
    (offset.hour ?? 0) * MS_PER_HOUR +
    (offset.minute ?? 0) * MS_PER_MINUTE +
    (offset.second ?? 0) * MS_PER_SECOND;

  return { time, truncatedOrigin };
};

/**
 * 時間経過によって 過去 > 未来 になりえるかの保守的な近似。
 * 開始だけが動く / 両端が動くが粒度が違う場合を「なりえる」とする
 * (開始が固定なら間隔は広がるだけ、同粒度なら間隔は一定)。
 */
const mayBecomeStartAfterEnd = (
  start: TadTimeEndpoint,
  end: TadTimeEndpoint
): boolean => {
  const startDrifts = start.origin === null;
  const endDrifts = end.origin === null;
  if (!startDrifts) return false;
  if (!endDrifts) return true;
  return driftGranularityOf(start) !== driftGranularityOf(end);
};

/**
 * `TadTimeRange`(相対式)を絶対時刻へ解決する。基準時刻は注入する(純関数)。
 *
 * @throws {TadTimeRangeResolveError} `getTadTimeRangeErrors` が空でないとき
 */
export const resolveTadTimeRange = (
  timeRange: TadTimeRange,
  now: Date
): ResolvedTadTimeRangeResult => {
  const errors = getTadTimeRangeErrors(timeRange);
  if (errors.length > 0) {
    throw new TadTimeRangeResolveError(errors.join("\n"));
  }
  const { start, end, timezone, limit } = timeRange;
  const nowMs = now.getTime();
  const warnings: TadTimeRangeWarning[] = [];

  const resolvedStart = start ? resolveEndpoint(start, nowMs, timezone) : null;
  const resolvedEnd = end ? resolveEndpoint(end, nowMs, timezone) : null;

  if (resolvedStart?.truncatedOrigin || resolvedEnd?.truncatedOrigin) {
    warnings.push("origin-component-truncated");
  }
  if (
    resolvedStart !== null &&
    resolvedEnd !== null &&
    resolvedStart.time > resolvedEnd.time
  ) {
    warnings.push("start-after-end");
  } else if (
    start !== null &&
    end !== null &&
    mayBecomeStartAfterEnd(start, end)
  ) {
    warnings.push("may-become-start-after-end");
  }

  // 動く端点のうち、次に境界を越えるのが早いほうを採用する
  const boundaries = [
    start && start.origin === null
      ? nextBoundaryOf(nowMs, timezone, driftGranularityOf(start))
      : null,
    end && end.origin === null
      ? nextBoundaryOf(nowMs, timezone, driftGranularityOf(end))
      : null,
  ].filter((v): v is number => v !== null);
  const nextBoundaryAt =
    boundaries.length === 0 ? null : Math.min(...boundaries);

  return {
    timeRange: {
      // isClosed → isExclusive で論理が反転する
      startsAt: resolvedStart
        ? { time: resolvedStart.time, isExclusive: !start!.isClosed }
        : null,
      endsAt: resolvedEnd
        ? { time: resolvedEnd.time, isExclusive: !end!.isClosed }
        : null,
    },
    limit,
    evaluatedAt: nowMs,
    nextBoundaryAt,
    warnings,
  };
};

const toAbsoluteTadTimeEndpoint = (
  resolved: ResolvedTadTimeEndpoint | null,
  timezone: TadTimeZone
): TadTimeEndpoint | null =>
  resolved === null
    ? null
    : {
        isClosed: !resolved.isExclusive,
        origin: formatTadInstant(resolved.time, timezone),
        // 全成分を相対 0 にすると原点がそのまま端点になる (null は絶対値なので使えない)
        offsetYears: 0,
        offsetMonths: 0,
        offsetDays: 0,
        offsetHours: 0,
        offsetMinutes: 0,
        offsetSeconds: 0,
      };

/**
 * 相対式の時間範囲を、解決済みの絶対時刻を原点に持つ等価な範囲へ変換する。
 *
 * api-server は受け取った式をリクエスト受信時刻で解決し直すため、相対指定のまま送ると
 * 画面に出した範囲と実際に処理される範囲がずれる。範囲置換のように「利用者が確認した
 * 範囲と実行される範囲が一致していること」が要る経路では、これを通してから送る。
 *
 * @throws {TadTimeRangeResolveError} `getTadTimeRangeErrors` が空でないとき
 */
export const toAbsoluteTadTimeRange = (
  timeRange: TadTimeRange,
  now: Date
): TadTimeRange => {
  const { timeRange: resolved } = resolveTadTimeRange(timeRange, now);
  return {
    start: toAbsoluteTadTimeEndpoint(resolved.startsAt, timeRange.timezone),
    end: toAbsoluteTadTimeEndpoint(resolved.endsAt, timeRange.timezone),
    timezone: timeRange.timezone,
    limit: timeRange.limit,
  };
};
